import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, AlertTriangle, TrendingUp, Globe2, FileWarning, 
  CheckCircle, XCircle, Clock, ChevronRight, Plus, Search,
  BarChart3, Building2, RefreshCw, Bell, AlertOctagon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GovernanceConsole() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [breaches, setBreaches] = useState([]);
  const [complianceRules, setComplianceRules] = useState([]);
  const [selectedBreach, setSelectedBreach] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [grsResult, setGrsResult] = useState(null);
  const [calculatingGRS, setCalculatingGRS] = useState(false);
  
  // Compliance Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState(null);

  useEffect(() => {
    // Fetch dashboard data - API will return 403 if not super_admin
    fetchDashboardData();
    fetchBreaches();
    fetchComplianceRules();
    fetchAlerts();
    fetchAlertStats();
  }, [user, navigate]);
  
  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gois/alerts/active`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };
  
  const fetchAlertStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gois/alerts/statistics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlertStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch alert stats:', error);
    }
  };
  
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/gois/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Acknowledged from console' })
      });
      if (response.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
        fetchAlertStats();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };
  
  const handleResolveAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/gois/alerts/${alertId}/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution_notes: 'Resolved from governance console' })
      });
      if (response.ok) {
        toast.success('Alert resolved');
        fetchAlerts();
        fetchAlertStats();
      }
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gois/governance/dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else if (response.status === 403) {
        toast.error('Access restricted to MediaKshetra governance team');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBreaches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gois/governance/breaches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBreaches(data.breaches || []);
      }
    } catch (error) {
      console.error('Failed to fetch breaches:', error);
    }
  };

  const fetchComplianceRules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gois/governance/rules`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setComplianceRules(data.rules || []);
      }
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    }
  };

  const handleEscalateBreach = async (breachId) => {
    try {
      const response = await fetch(`${API_URL}/api/gois/governance/breaches/${breachId}/escalate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Escalated from governance console' })
      });
      if (response.ok) {
        toast.success('Breach escalated successfully');
        fetchBreaches();
      }
    } catch (error) {
      toast.error('Failed to escalate breach');
    }
  };

  const handleResolveBreach = async (breachId) => {
    try {
      const response = await fetch(`${API_URL}/api/gois/governance/breaches/${breachId}/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolution_notes: 'Resolved from governance console' })
      });
      if (response.ok) {
        toast.success('Breach resolved successfully');
        fetchBreaches();
        setSelectedBreach(null);
      }
    } catch (error) {
      toast.error('Failed to resolve breach');
    }
  };

  const calculateSampleGRS = async () => {
    setCalculatingGRS(true);
    try {
      const response = await fetch(`${API_URL}/api/gois/governance/grs`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_id: 'sample_client',
          platforms: ['google_ads', 'meta'],
          regions: ['US', 'GB'],
          vertical: 'ecommerce'
        })
      });
      if (response.ok) {
        const data = await response.json();
        setGrsResult(data);
      }
    } catch (error) {
      toast.error('Failed to calculate GRS');
    } finally {
      setCalculatingGRS(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'moderate': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-zinc-400';
    }
  };

  const filteredBreaches = breaches.filter(breach => 
    breach.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    breach.client_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    breach.breach_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#D4A84B]" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = dashboardData?.breach_statistics || {};
  const heatmap = dashboardData?.compliance_heatmap || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="governance-console">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="h-7 w-7 text-[#D4A84B]" />
              Governance Console
            </h1>
            <p className="text-zinc-400 mt-1">MediaKshetra Internal - Compliance & Risk Management</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              onClick={() => { fetchDashboardData(); fetchBreaches(); }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Breaches</p>
                  <p className="text-3xl font-bold text-white">{stats.total_breaches || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                  <FileWarning className="h-6 w-6 text-zinc-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Open Breaches</p>
                  <p className="text-3xl font-bold text-red-400">{stats.open_breaches || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Escalated</p>
                  <p className="text-3xl font-bold text-orange-400">{stats.escalated_breaches || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Resolved</p>
                  <p className="text-3xl font-bold text-green-400">{stats.resolved_breaches || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              Overview
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              <Bell className="h-4 w-4 mr-1" />
              Alerts {alerts.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{alerts.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="breaches" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              Breaches
            </TabsTrigger>
            <TabsTrigger value="heatmap" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              Compliance Heatmap
            </TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              Rules Database
            </TabsTrigger>
            <TabsTrigger value="grs" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              GRS Calculator
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Severity Distribution */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white">Breaches by Severity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.by_severity || {}).map(([severity, count]) => (
                      <div key={severity} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(severity)}>
                            {severity.charAt(0).toUpperCase() + severity.slice(1)}
                          </Badge>
                        </div>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Type Distribution */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white">Breaches by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.by_type || {}).slice(0, 5).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-zinc-400 text-sm capitalize">
                          {type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Regulations Tracked */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Globe2 className="h-5 w-5 text-[#D4A84B]" />
                    Regulations Tracked
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {(dashboardData?.regulations || []).map(reg => (
                      <Badge key={reg} variant="outline" className="border-[#D4A84B]/30 text-[#D4A84B]">
                        {reg}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Breaches */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white">Recent Open Breaches</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(dashboardData?.recent_open_breaches || []).slice(0, 4).map(breach => (
                      <div key={breach.breach_id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(breach.severity)} variant="outline">
                            {breach.severity}
                          </Badge>
                          <span className="text-zinc-300 text-sm truncate max-w-[200px]">
                            {breach.description}
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-zinc-500" />
                      </div>
                    ))}
                    {(!dashboardData?.recent_open_breaches || dashboardData.recent_open_breaches.length === 0) && (
                      <p className="text-zinc-500 text-center py-4">No open breaches</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Real-time Compliance Alerts Tab */}
          <TabsContent value="alerts" className="space-y-4">
            {/* Alert Stats Summary */}
            {alertStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-zinc-400 text-sm">Active Alerts</p>
                      <p className="text-3xl font-bold text-red-400">{alerts.length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-zinc-400 text-sm">Critical</p>
                      <p className="text-3xl font-bold text-red-500">{alertStats.by_severity?.critical || 0}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-zinc-400 text-sm">High</p>
                      <p className="text-3xl font-bold text-orange-400">{alertStats.by_severity?.high || 0}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-zinc-400 text-sm">30-Day Total</p>
                      <p className="text-3xl font-bold text-white">{alertStats.total_alerts}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertOctagon className="h-5 w-5 text-red-400" />
                  Active Compliance Alerts
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Real-time alerts when campaigns approach policy thresholds
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length > 0 ? (
                  <div className="space-y-3">
                    {alerts.map(alert => (
                      <div key={alert.alert_id} className="p-4 bg-zinc-800/50 rounded-lg border-l-4" style={{ borderLeftColor: alert.severity === 'critical' ? '#ef4444' : alert.severity === 'high' ? '#f97316' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getSeverityColor(alert.severity)}>
                                {alert.severity}
                              </Badge>
                              <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                                {alert.category?.replace(/_/g, ' ')}
                              </Badge>
                            </div>
                            <h4 className="text-white font-medium">{alert.title}</h4>
                            <p className="text-zinc-400 text-sm mt-1">{alert.message}</p>
                            {alert.recommended_action && (
                              <p className="text-[#D4A84B] text-sm mt-2">
                                <span className="font-medium">Action:</span> {alert.recommended_action}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-zinc-500 text-xs">
                              <span>Client: {alert.client_id}</span>
                              {alert.platform_id && <span>Platform: {alert.platform_id}</span>}
                              {alert.threshold_proximity && <span>Threshold: {Math.round(alert.threshold_proximity * 100)}%</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                              onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                            >
                              Acknowledge
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleResolveAlert(alert.alert_id)}
                            >
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-white font-medium">No Active Alerts</p>
                    <p className="text-zinc-400 text-sm mt-1">All campaigns are operating within compliance thresholds</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Breaches Tab */}
          <TabsContent value="breaches" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder="Search breaches..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
            </div>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-0">
                <div className="divide-y divide-zinc-800">
                  {filteredBreaches.map(breach => (
                    <div 
                      key={breach.breach_id} 
                      className="p-4 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedBreach(breach)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge className={getSeverityColor(breach.severity)}>
                              {breach.severity}
                            </Badge>
                            <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                              {breach.breach_type?.replace(/_/g, ' ')}
                            </Badge>
                            {breach.escalation_status === 'escalated' && (
                              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                                Escalated
                              </Badge>
                            )}
                          </div>
                          <p className="text-white">{breach.description}</p>
                          <p className="text-zinc-500 text-sm">
                            Client: {breach.client_id} | Platform: {breach.platform_id || 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!breach.resolved_flag && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                                onClick={(e) => { e.stopPropagation(); handleEscalateBreach(breach.breach_id); }}
                                disabled={breach.escalation_status === 'escalated'}
                              >
                                Escalate
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={(e) => { e.stopPropagation(); handleResolveBreach(breach.breach_id); }}
                              >
                                Resolve
                              </Button>
                            </>
                          )}
                          {breach.resolved_flag && (
                            <Badge className="bg-green-500/20 text-green-400">Resolved</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredBreaches.length === 0 && (
                    <div className="p-8 text-center">
                      <FileWarning className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400">No breaches found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Heatmap Tab */}
          <TabsContent value="heatmap" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Regional Risk */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Globe2 className="h-5 w-5 text-[#D4A84B]" />
                    Risk by Region
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(heatmap.by_region || {}).map(([code, data]) => (
                      <div key={code} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{data.name}</p>
                          <p className="text-zinc-500 text-sm">
                            {data.regulations?.join(', ') || 'No specific regulations'}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={getSeverityColor(data.risk_level)}>
                            {data.risk_level}
                          </Badge>
                          <p className="text-zinc-400 text-sm mt-1">
                            {data.breach_count} breaches
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Platform Risk */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[#D4A84B]" />
                    Risk by Platform
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(heatmap.by_platform || {}).map(([platform, data]) => (
                      <div key={platform} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{data.name}</p>
                          <p className="text-zinc-500 text-sm">
                            {data.restricted_categories} restricted categories
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={getSeverityColor(data.risk_level)}>
                            {data.risk_level}
                          </Badge>
                          <p className="text-zinc-400 text-sm mt-1">
                            {data.breach_count} breaches
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Rules Database Tab */}
          <TabsContent value="rules" className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Compliance Rules Database</CardTitle>
                <CardDescription className="text-zinc-400">
                  Foundational schema for market, platform, and vertical compliance rules
                </CardDescription>
              </CardHeader>
              <CardContent>
                {complianceRules.length > 0 ? (
                  <div className="space-y-3">
                    {complianceRules.map(rule => (
                      <div key={rule.rule_id} className="p-4 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-white font-medium">{rule.rule_name}</h4>
                            <p className="text-zinc-400 text-sm mt-1">{rule.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="border-zinc-700">
                                {rule.rule_type}
                              </Badge>
                              <Badge className={getSeverityColor(rule.severity)}>
                                {rule.severity}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 mb-2">No compliance rules configured yet</p>
                    <p className="text-zinc-500 text-sm">
                      Rules can be added via the API to define market, platform, and vertical compliance requirements
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GRS Calculator Tab */}
          <TabsContent value="grs" className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#D4A84B]" />
                  Governance Risk Score Calculator
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Calculate risk scores based on platforms, regions, and vertical
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button 
                    onClick={calculateSampleGRS}
                    disabled={calculatingGRS}
                    className="bg-[#D4A84B] hover:bg-[#c99b3f] text-black"
                  >
                    {calculatingGRS ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <BarChart3 className="h-4 w-4 mr-2" />
                    )}
                    Calculate Sample GRS
                  </Button>

                  {grsResult && (
                    <div className="mt-6 p-6 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <p className="text-zinc-400 text-sm">Governance Risk Score</p>
                          <p className={`text-4xl font-bold ${getRiskLevelColor(grsResult.risk_level)}`}>
                            {grsResult.grs_score}
                          </p>
                        </div>
                        <Badge 
                          className={`text-lg px-4 py-2 ${getSeverityColor(grsResult.risk_level)}`}
                          style={{ backgroundColor: grsResult.color + '20', color: grsResult.color }}
                        >
                          {grsResult.risk_level?.toUpperCase()}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        {Object.entries(grsResult.components || {}).map(([key, value]) => (
                          <div key={key} className="text-center p-3 bg-zinc-900/50 rounded-lg">
                            <p className="text-zinc-400 text-xs capitalize">
                              {key.replace(/_/g, ' ')}
                            </p>
                            <p className="text-white text-xl font-bold">{value}</p>
                          </div>
                        ))}
                      </div>

                      <div>
                        <p className="text-zinc-400 text-sm mb-2">Recommendations</p>
                        <ul className="space-y-2">
                          {grsResult.recommendations?.map((rec, i) => (
                            <li key={i} className="flex items-start gap-2 text-zinc-300">
                              <ChevronRight className="h-4 w-4 mt-0.5 text-[#D4A84B]" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Selected Breach Modal */}
        {selectedBreach && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setSelectedBreach(null)}>
            <Card className="bg-zinc-900 border-zinc-800 max-w-2xl w-full mx-4" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Breach Details</span>
                  <Badge className={getSeverityColor(selectedBreach.severity)}>
                    {selectedBreach.severity}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-zinc-400 text-sm">Description</p>
                  <p className="text-white">{selectedBreach.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-400 text-sm">Client ID</p>
                    <p className="text-white">{selectedBreach.client_id}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Platform</p>
                    <p className="text-white">{selectedBreach.platform_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Type</p>
                    <p className="text-white capitalize">{selectedBreach.breach_type?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Status</p>
                    <p className="text-white">{selectedBreach.resolved_flag ? 'Resolved' : 'Open'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Detected By</p>
                    <p className="text-white">{selectedBreach.detected_by}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Timestamp</p>
                    <p className="text-white">{new Date(selectedBreach.timestamp).toLocaleString()}</p>
                  </div>
                </div>
                {selectedBreach.details && Object.keys(selectedBreach.details).length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-sm mb-2">Additional Details</p>
                    <pre className="text-zinc-300 text-sm bg-zinc-800/50 p-3 rounded-lg overflow-x-auto">
                      {JSON.stringify(selectedBreach.details, null, 2)}
                    </pre>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setSelectedBreach(null)}>
                    Close
                  </Button>
                  {!selectedBreach.resolved_flag && (
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleResolveBreach(selectedBreach.breach_id)}
                    >
                      Resolve Breach
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
